import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Button, DataTable, EstadoVazio, KpiCard, Pill, fmtBRL, type Column } from "@atlas/ui/web";
import type { EstadoArquivoDoMes, OrgaoDoMesOperadora } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { bancoPermissaoCobre, readBancoPermissoesDaSessao } from "../../lib/banco-perms";
import { mensagemDeErro } from "../../lib/erro-validacao";

// A VISÃO DO MÊS DA CONSIGNATÁRIA (#314, epic #312).
//
// A entrada de quem opera benefícios. O vínculo dela com cada órgão/entidade é a
// RUBRICA — por isso esta tela nunca fala em convênio. Por órgão: o arquivo
// mensal da competência, o corte, o pedido × descontado do último arquivo
// confirmado e o que espera por ela (alerta de arquivo, pedidos de cancelamento,
// autorizações do servidor).

const reais = (centavos: number) => fmtBRL(centavos / 100);
const mesAno = (aaaamm: string) => `${aaaamm.slice(4, 6)}/${aaaamm.slice(0, 4)}`;

const ARQUIVO: Record<EstadoArquivoDoMes, { rotulo: string; variante: "averbado" | "pendente" | "expirado" }> = {
  confirmado: { rotulo: "Confirmado", variante: "averbado" },
  aguardando_confirmacao: { rotulo: "Aguardando confirmação", variante: "pendente" },
  nao_enviado: { rotulo: "Não enviado", variante: "expirado" },
};

function dataHora(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
}

function Pendencias({ o }: { o: OrgaoDoMesOperadora }) {
  const p = o.pendencias;
  const itens: { texto: string; grave: boolean; href?: string }[] = [];
  if (p.alertaArquivo) {
    itens.push({
      texto: p.alertaArquivo.severidade === "critico" ? "Arquivo pendente — véspera do corte" : "Arquivo pendente antes do corte",
      grave: p.alertaArquivo.severidade === "critico",
    });
  }
  if (p.pedidosCancelamento > 0) {
    const vencidos = p.pedidosVencidos > 0 ? ` (${p.pedidosVencidos} com prazo vencido)` : "";
    itens.push({
      texto: `${p.pedidosCancelamento} pedido${p.pedidosCancelamento === 1 ? "" : "s"} de cancelamento${vencidos}`,
      grave: p.pedidosVencidos > 0,
      // #318: a fila onde se responde.
      href: "/banco/pedidos-cancelamento",
    });
  }
  const aguardando = p.inclusoesAguardando + p.aumentosAguardando;
  if (aguardando > 0) {
    itens.push({ texto: `${aguardando} aguardando autorização do servidor`, grave: false });
  }
  if (itens.length === 0) return <span style={{ color: "var(--text-dim)" }}>—</span>;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 2 }}>
      {itens.map((i) => (
        <li key={i.texto} style={{ color: i.grave ? "var(--danger-500)" : "var(--text)", fontWeight: i.grave ? 600 : 400 }}>
          {i.href ? <Link to={i.href} style={{ color: "inherit" }}>{i.texto}</Link> : i.texto}
        </li>
      ))}
    </ul>
  );
}

export function BancoVisaoDoMes() {
  const nav = useNavigate();
  const visaoQ = useQuery({
    queryKey: ["banco", "operadora", "visao-do-mes"],
    queryFn: () => atlas.banco.operadora.visaoDoMes(),
  });
  const permissoes = readBancoPermissoesDaSessao();
  const podeEnviar = bancoPermissaoCobre(permissoes, "arquivos-envio");
  const podeVerBeneficios = bancoPermissaoCobre(permissoes, "beneficios");

  if (visaoQ.isPending) return <div style={{ color: "var(--text-muted)" }}>Carregando a visão do mês…</div>;
  if (visaoQ.isError) {
    return <div role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(visaoQ.error, "Não foi possível carregar a visão do mês.")}</div>;
  }
  const v = visaoQ.data;

  const colunas: Column<OrgaoDoMesOperadora>[] = [
    {
      key: "orgao",
      header: "Órgão/entidade",
      wrap: true,
      // #316: o órgão abre os benefícios dele, para quem pode vê-los.
      render: (o) => (podeVerBeneficios ? <Link to={`/banco/beneficios?unidadeId=${o.unidadeId}`}>{o.orgao}</Link> : o.orgao),
    },
    { key: "rubricas", header: "Rubricas", mono: true, render: (o) => o.rubricas.join(", ") },
    {
      key: "corte",
      header: "Corte",
      render: (o) => (
        <span style={{ color: o.cortePassou ? "var(--text-dim)" : "var(--text)" }}>
          {dataHora(o.corteEm)}
          {o.cortePassou ? " (passou)" : ""}
        </span>
      ),
    },
    {
      key: "arquivo",
      header: "Arquivo do mês",
      render: (o) => {
        const a = ARQUIVO[o.arquivo.estado];
        // Sem obrigatoriedade, "não enviado" não é falta: o movimento repete o que está gravado.
        if (o.arquivo.estado === "nao_enviado" && !o.arquivoObrigatorio) {
          return <span style={{ color: "var(--text-dim)" }}>Não enviado (opcional)</span>;
        }
        return <Pill variant={a.variante}>{a.rotulo}</Pill>;
      },
    },
    {
      key: "ultimo",
      header: "Último confirmado",
      wrap: true,
      render: (o) => {
        const u = o.ultimoConfirmado;
        if (!u) return <span style={{ color: "var(--text-dim)" }}>—</span>;
        return (
          <span>
            {mesAno(u.competencia)}: pedido {reais(u.pedidoCentavos)}
            {" · "}
            {u.retornoImportado && u.descontadoCentavos != null
              ? `descontado ${reais(u.descontadoCentavos)}`
              : <span style={{ color: "var(--text-dim)" }}>retorno ainda não importado</span>}
          </span>
        );
      },
    },
    { key: "pendencias", header: "Pendências", wrap: true, render: (o) => <Pendencias o={o} /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <header style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Benefícios
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>Visão do mês — {mesAno(v.competencia)}</h1>
          <div style={{ color: "var(--text-muted)" }}>
            Os órgãos/entidades em que você tem rubrica: o arquivo do mês, o corte e o que espera por você.
          </div>
        </div>
        {podeEnviar ? <Button onClick={() => nav("/banco/troca-arquivos")}>Enviar arquivo mensal</Button> : null}
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <KpiCard label="Órgãos/entidades" value={v.totais.orgaos} hint="com rubrica sua vigente" />
        <KpiCard label="Arquivos confirmados" value={`${v.totais.arquivosConfirmados} de ${v.totais.orgaos}`} hint={`competência ${mesAno(v.competencia)}`} />
        <KpiCard
          label="Pedidos de cancelamento"
          value={v.totais.pedidosCancelamento}
          hint={v.totais.pedidosVencidos > 0 ? `${v.totais.pedidosVencidos} com prazo vencido` : "abertos"}
        />
        <KpiCard label="Aguardando autorização" value={v.totais.autorizacoesAguardando} hint="inclusões e aumentos" />
      </div>

      {v.orgaos.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum órgão/entidade vinculado por rubrica ainda"
          orientacao="Quando a averbadora classificar uma rubrica com você como consignatária, o órgão aparece aqui."
        />
      ) : (
        <DataTable columns={colunas} rows={v.orgaos} rowKey={(o) => String(o.unidadeId)} />
      )}
    </div>
  );
}
